'use strict';

/**
 * CAD-only donation type dropdown on classic checkout.
 * When the block is hidden (non-CAD), `name` is removed from the hidden input so it is not POSTed.
 */
export default class DonationTypeDropdown {
	constructor() {
		this.donationTypeWrapper = document.querySelector(
			'.donation-type-dropdown-wrapper'
		);
		if ( ! this.donationTypeWrapper ) {
			return;
		}

		this.customSelect = document.querySelector( '#donation_type_custom_select' );
		this.trigger = this.customSelect?.querySelector( '.lc-custom-select__trigger' );
		this.valueDisplay = this.customSelect?.querySelector( '.lc-custom-select__value' );
		this.hiddenInput = document.querySelector( '#donation_type_select' );
		this.orgCheckboxWrapper = document.querySelector(
			'.organization_donation_wrapper'
		);
		this.charCheckboxWrapper = document.querySelector(
			'.charitable_foundation_wrapper'
		);
		this.orgCheckbox = document.querySelector( '#organization_donation' );
		this.charCheckbox = document.querySelector( '#charitable_foundation' );
		this.billingCompany = document.querySelector( '#billing_company_field' );
		this.billingCompanyInput = this.billingCompany?.querySelector( 'input' );
		this.billingCompanyLabel = this.billingCompany?.querySelector( 'label' );
		this.currencySelect = document.querySelector(
			'[name="user_paying_currency"]'
		);

		this._bind();
		this.toggleDisplay();
	}

	getCurrentCurrency() {
		if ( this.currencySelect ) {
			return this.currencySelect.value;
		}
		const checkoutForm = document.querySelector( 'form[name="checkout"]' );
		return checkoutForm?.dataset?.currency || '';
	}

	isCAD() {
		return this.getCurrentCurrency() === 'CAD';
	}

	/**
	 * When donation type is not applicable (non-CAD), remove `name` so the field is omitted from POST.
	 */
	stripDonationTypeFieldName() {
		if ( ! this.hiddenInput ) {
			return;
		}
		const name = this.hiddenInput.getAttribute( 'name' );
		if ( name ) {
			this.hiddenInput.dataset.levcharityNameBackup = name;
			this.hiddenInput.removeAttribute( 'name' );
		}
	}

	/**
	 * Restore `name` when CAD is active so donation_type is submitted again.
	 */
	restoreDonationTypeFieldName() {
		if ( ! this.hiddenInput ) {
			return;
		}
		const backup =
			this.hiddenInput.dataset.levcharityNameBackup || 'donation_type';
		this.hiddenInput.setAttribute( 'name', backup );
	}

	toggleDisplay() {
		if ( this.isCAD() ) {
			this.restoreDonationTypeFieldName();
			this.donationTypeWrapper.style.display = '';
		} else {
			this.resetDropdown();
			this.stripDonationTypeFieldName();
			this.donationTypeWrapper.style.display = 'none';
		}
		if ( this.orgCheckboxWrapper ) {
			this.orgCheckboxWrapper.style.display = 'none';
		}
		if ( this.charCheckboxWrapper ) {
			this.charCheckboxWrapper.style.display = 'none';
		}
	}

	resetDropdown() {
		if ( this.hiddenInput ) {
			this.hiddenInput.value = '';
		}
		if ( this.valueDisplay ) {
			this.valueDisplay.textContent = 'Select one';
		}
		this.customSelect?.classList.remove( 'is-open' );
		this.customSelect?.querySelectorAll( '.lc-custom-select__option' ).forEach(
			( opt ) => opt.classList.remove( 'is-selected' )
		);
		if ( this.orgCheckbox && this.orgCheckbox.checked ) {
			this.orgCheckbox.checked = false;
			this.triggerCheckboxChange( this.orgCheckbox );
		}
		if ( this.charCheckbox && this.charCheckbox.checked ) {
			this.charCheckbox.checked = false;
			this.triggerCheckboxChange( this.charCheckbox );
		}
		if ( this.billingCompany ) {
			this.billingCompany.classList.add( 'hidden' );
		}
	}

	triggerCheckboxChange( checkbox ) {
		if ( ! checkbox ) {
			return;
		}
		checkbox.dispatchEvent(
			new Event( 'change', { bubbles: true, cancelable: true } )
		);
	}

	triggerHiddenInputChange() {
		if ( ! this.hiddenInput ) {
			return;
		}
		this.hiddenInput.dispatchEvent(
			new Event( 'change', { bubbles: true, cancelable: true } )
		);
	}

	clearValidationError() {
		this.donationTypeWrapper.classList.remove(
			'woocommerce-invalid',
			'woocommerce-invalid-required-field'
		);
		const errorMsg = this.donationTypeWrapper.querySelector(
			'.donation-type-error'
		);
		if ( errorMsg ) {
			errorMsg.remove();
		}
	}

	showValidationError() {
		this.donationTypeWrapper.classList.add(
			'woocommerce-invalid',
			'woocommerce-invalid-required-field'
		);
		if ( ! this.donationTypeWrapper.querySelector( '.donation-type-error' ) ) {
			const errorEl = document.createElement( 'span' );
			errorEl.className = 'donation-type-error';
			errorEl.style.cssText =
				'color:#e2401c;font-size:0.85em;display:block;margin-top:4px;';
			errorEl.textContent = 'Please select a Donation Type.';
			this.donationTypeWrapper.appendChild( errorEl );
		}
	}

	isCompanyRequired() {
		return (
			this.hiddenInput &&
			( this.hiddenInput.value === 'business' ||
				this.hiddenInput.value === 'charitable' )
		);
	}

	clearCompanyValidationError() {
		if ( ! this.billingCompany ) {
			return;
		}
		this.billingCompany.classList.remove(
			'woocommerce-invalid',
			'woocommerce-invalid-required-field'
		);
		const errorMsg = this.billingCompany.querySelector( '.company-field-error' );
		if ( errorMsg ) {
			errorMsg.remove();
		}
		if ( this.billingCompanyInput ) {
			this.billingCompanyInput.classList.remove( 'field-error' );
		}
	}

	showCompanyValidationError() {
		if ( ! this.billingCompany ) {
			return;
		}
		this.billingCompany.classList.add(
			'woocommerce-invalid',
			'woocommerce-invalid-required-field'
		);
		if ( this.billingCompanyInput ) {
			this.billingCompanyInput.classList.add( 'field-error' );
		}
		if ( ! this.billingCompany.querySelector( '.company-field-error' ) ) {
			const label =
				this.hiddenInput?.value === 'charitable'
					? 'Foundation Name'
					: 'Company Name';
			const errorEl = document.createElement( 'span' );
			errorEl.className = 'company-field-error';
			errorEl.style.cssText =
				'color:#e2401c;font-size:0.85em;display:block;margin-top:4px;';
			errorEl.textContent = label + ' is a required field.';
			this.billingCompany.appendChild( errorEl );
		}
	}

	selectOption( value, label ) {
		if ( this.hiddenInput ) {
			this.hiddenInput.value = value;
		}
		if ( this.valueDisplay ) {
			this.valueDisplay.textContent = label;
		}
		this.customSelect?.classList.remove( 'is-open' );
		this.customSelect?.querySelectorAll( '.lc-custom-select__option' ).forEach(
			( opt ) => {
				opt.classList.toggle( 'is-selected', opt.dataset.value === value );
			}
		);
		this.clearValidationError();
		this.handleDonationTypeChange( value );
		this.triggerHiddenInputChange();
	}

	handleDonationTypeChange( value ) {
		switch ( value ) {
			case 'business':
				if ( this.orgCheckbox && ! this.orgCheckbox.checked ) {
					this.orgCheckbox.checked = true;
					this.triggerCheckboxChange( this.orgCheckbox );
				}
				if ( this.charCheckbox && this.charCheckbox.checked ) {
					this.charCheckbox.checked = false;
					this.triggerCheckboxChange( this.charCheckbox );
				}
				if ( this.billingCompany ) {
					this.billingCompany.classList.remove( 'hidden' );
				}
				if ( this.billingCompanyInput ) {
					this.billingCompanyInput.placeholder = 'Company Name *';
				}
				if ( this.billingCompanyLabel ) {
					this.billingCompanyLabel.innerHTML =
						'Company Name <abbr class="required" title="required">*</abbr>';
				}
				this.clearCompanyValidationError();
				break;

			case 'charitable':
				if ( this.charCheckbox && ! this.charCheckbox.checked ) {
					this.charCheckbox.checked = true;
					this.triggerCheckboxChange( this.charCheckbox );
				}
				if ( this.orgCheckbox && this.orgCheckbox.checked ) {
					this.orgCheckbox.checked = false;
					this.triggerCheckboxChange( this.orgCheckbox );
				}
				if ( this.billingCompany ) {
					this.billingCompany.classList.remove( 'hidden' );
				}
				if ( this.billingCompanyInput ) {
					this.billingCompanyInput.placeholder = 'Foundation Name *';
				}
				if ( this.billingCompanyLabel ) {
					this.billingCompanyLabel.innerHTML =
						'Foundation Name <abbr class="required" title="required">*</abbr>';
				}
				this.clearCompanyValidationError();
				break;

			case 'individual':
			default:
				if ( this.orgCheckbox && this.orgCheckbox.checked ) {
					this.orgCheckbox.checked = false;
					this.triggerCheckboxChange( this.orgCheckbox );
				}
				if ( this.charCheckbox && this.charCheckbox.checked ) {
					this.charCheckbox.checked = false;
					this.triggerCheckboxChange( this.charCheckbox );
				}
				if ( this.billingCompany ) {
					this.billingCompany.classList.add( 'hidden' );
				}
				break;
		}
	}

	_bind() {
		if ( this.billingCompanyInput ) {
			this.billingCompanyInput.addEventListener( 'input', () => {
				if ( this.billingCompanyInput.value.trim() ) {
					this.clearCompanyValidationError();
				}
			} );
			this.billingCompanyInput.addEventListener( 'blur', () => {
				if (
					this.isCompanyRequired() &&
					! this.billingCompanyInput.value.trim()
				) {
					this.showCompanyValidationError();
				}
			} );
		}

		this.trigger?.addEventListener( 'click', () =>
			this.customSelect?.classList.toggle( 'is-open' )
		);

		this.trigger?.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				e.preventDefault();
				this.customSelect?.classList.toggle( 'is-open' );
			}
		} );

		this.customSelect?.querySelectorAll( '.lc-custom-select__option' ).forEach(
			( option ) => {
				option.addEventListener( 'click', () =>
					this.selectOption(
						option.dataset.value,
						option.textContent.trim()
					)
				);
			}
		);

		document.addEventListener( 'click', ( e ) => {
			if ( ! this.customSelect?.contains( e.target ) ) {
				this.customSelect?.classList.remove( 'is-open' );
			}
		} );

		if ( this.currencySelect ) {
			this.currencySelect.addEventListener( 'change', () =>
				this.toggleDisplay()
			);
		}

		jQuery?.( document.body ).on( 'updated_checkout', () =>
			this.toggleDisplay()
		);

		if ( typeof jQuery !== 'undefined' ) {
			const self = this;
			jQuery( 'form.checkout' ).on( 'checkout_place_order', function () {
				if ( self.donationTypeWrapper.style.display === 'none' ) {
					self.stripDonationTypeFieldName();
					return true;
				}
				if ( ! self.hiddenInput || ! self.hiddenInput.value ) {
					self.showValidationError();
					self.donationTypeWrapper.scrollIntoView( {
						behavior: 'smooth',
						block: 'center',
					} );
					return false;
				}
				self.clearValidationError();

				if (
					self.isCompanyRequired() &&
					self.billingCompanyInput &&
					! self.billingCompanyInput.value.trim()
				) {
					self.showCompanyValidationError();
					self.billingCompany.scrollIntoView( {
						behavior: 'smooth',
						block: 'center',
					} );
					return false;
				}
				self.clearCompanyValidationError();
				return true;
			} );
		}
	}
}
